import type { EnchantmentEligibilityFacts, validateEnchantment } from '../janus-engine/compile/validate-enchantment';
import { enchantmentTemplateDisplayTitle } from '../janus-engine/compile/enchantment-template';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { decodeHtmlEntitiesForPlainText } from './decode-html-entities';

export interface EnchantmentValidationResult { valid: boolean; message: string; display_title?: string }
const validUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** Stateless across bundles: the server supplies authored rows, this browser decides. */
export async function checkInventoryEnchantment(itemId: string, templateId: string,
    url = '/options/tools/inventory/validate_enchantment_inventory_inc.php'): Promise<EnchantmentValidationResult> {
    if (!templateId) return { valid: true, message: '' };
    if (!validUuid(itemId)) return { valid: false, message: 'Item template ID is missing or not a valid UUID.' };
    if (!validUuid(templateId)) return { valid: false, message: 'Enchantment template ID must be a valid UUID.' };
    const response = await fetch(new URL(url, document.baseURI), { method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventory_item_id: itemId, enchantment_template_id: templateId }) });
    if (!response.ok) throw new Error(`Enchantment facts HTTP ${response.status}`);
    const facts = await response.json() as EnchantmentEligibilityFacts & { schema: string; display_title: string };
    if (facts.schema !== 'enchantment-eligibility-facts.v1' || !Array.isArray(facts.members)
        || !facts.definitions || !facts.catalogRows) throw new Error('Enchantment facts are unavailable.');
    const verdict = window.ftValidateEnchantment(itemId, facts);
    const title = enchantmentTemplateDisplayTitle(facts.members.filter(row => !Number(readItemField(row, 'enchantment_template_enchantment_deleted') ?? 0)));
    return { valid: verdict === true, message: verdict === true ? '' : verdict,
        display_title: decodeHtmlEntitiesForPlainText(title || facts.display_title) };
}

export async function allowInventoryEnchantment(itemId: string, templateId: string): Promise<boolean> {
    try {
        const result = await checkInventoryEnchantment(itemId, templateId);
        if (result.valid) return true;
        window.displayWarning(result.message);
    } catch {
        window.displayWarning('Could not validate enchantment template. Check your connection and try again.');
    }
    return false;
}

declare global {
    interface Window {
        ftValidateEnchantment: typeof validateEnchantment;
        ftCheckInventoryEnchantment: typeof checkInventoryEnchantment;
        ftAllowInventoryEnchantment: typeof allowInventoryEnchantment;
    }
}
